import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Button, IconButton, ResponsiveDialog, Stack, styled, Text } from 'shell/components';

const PullAssignmentDialog = ({ open, onClose, onSave, assignments, fetchedAssignments }) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    const [assignmentList, setAssignmentList] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);

    const handleClose = () => {
        setSelectedAssignment(null);
        closeDialog();
        onClose();
    };

    const onChangeAssignment = (selectedAssignment) => {
        const findAssignment = assignmentList.find((assignment) => assignment.key === selectedAssignment.key);
        setSelectedAssignment(findAssignment);
    };

    const getErrors = (assignment) => {
        let errors = [];
        const platform = process.env.REACT_APP_KIND;
        if (platform === 'D2L') {
            const isMissingTitle = assignment?.Name;
            if (!isMissingTitle) errors.push(t('assignment-title-label', 'Assignment title'));
        }
        return errors;
    };

    const handleClickSave = () => {
        const findAssignmentData = fetchedAssignments?.find(({ id }) => selectedAssignment.key === id);
        const assignmentDuplication = assignments?.find(
            (assignment) => assignment.api_assignment_id === findAssignmentData.id
        );
        if (assignmentDuplication) {
            return message.error(t('import-assignment.already-created-error', 'The assignment is already created.'));
        }
        let errors = getErrors(findAssignmentData);

        if (errors.length) {
            let errorList = '';
            errors.forEach((error) => {
                errorList += `· ${error}\n`;
            });
            const errorMessage = (
                <Stack spacing={2}>
                    <Text>
                        {t(
                            'import-assignment.error-title',
                            'Please add the following information to the assignment on your LMS and try importing again:'
                        )}
                    </Text>
                    <Text>{errorList}</Text>
                </Stack>
            );

            setDialog({
                open: true,
                message: t('dialog.missing-information.title', 'Missing Information Required for Imported Assignments'),
                description: errorMessage,
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: () => {
                    handleClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => handleClose(),
            });
        } else {
            onSave(findAssignmentData);
            handleClose();
        }
    };

    useEffect(() => {
        if (open) {
            if (fetchedAssignments.length) {
                const mapAssignments = fetchedAssignments.map((assignment) => {
                    return {
                        key: assignment.id,
                        value: assignment.name,
                    };
                });

                setAssignmentList(mapAssignments);
            }
        }
    }, [open, fetchedAssignments]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <TitleStack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'} weight={'medium'}>
                        {t('import-assignment.select-title', 'Select an Assignment to Import')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Text>
                    {t(
                        'import-assignment.select-description',
                        'The assignment on your LMS must contain the full assignment title, description, and rubric. Changes must be made on your LMS before syncing them to the assignment on TimelyGrader.'
                    )}{' '}
                </Text>
            </TitleStack>
            <CustomDialogContent device={device}>
                <Autocomplete
                    disableClearable
                    error={false}
                    helperText={''}
                    label={t('assignment-name-label', 'Assignment Name')}
                    options={assignmentList}
                    placeholder={t('common:ui.select', 'Select')}
                    required
                    value={selectedAssignment}
                    onChange={(_, value) => onChangeAssignment(value)}
                />
            </CustomDialogContent>
            <BottomStack alignItems={'center'} direction={'row'} justifyContent={'flex-end'}>
                <Button onClick={handleClickSave}>{t('common:controls.import', 'Import')}</Button>
            </BottomStack>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '600px',
            height: '295px',
        },
    };
});

const TitleStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(7),
    };
});

const BottomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(0, 7, 0, 0),
    };
});

const CustomDialogContent = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(0, 7, 7, 7),
    };
});

export default PullAssignmentDialog;
